import React, { useEffect, useState } from "react";
import { guardianAlertService } from "../../services/guardianAlertService";
import RichTextView from "../RichTextEditor/RichTextView";
import {
  Overlay,
  ModalContainer,
  ModalHeader,
  HeaderText,
  ModalTitle,
  ModalSubtitle,
  CloseButton,
  ModalBody,
  Section,
  SectionTitle,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  DescriptionBlock,
  DescriptionText,
  BadgeRow,
  StatusBadge,
  ModalFooter,
  FooterButton,
} from "./styles";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch {
    return String(value);
  }
}

function getResolutionMeta(resolution) {
  if (resolution === "created") {
    return { label: "Alerta Criado", tone: "success" };
  }
  if (resolution === "rejected") {
    return { label: "Rejeitado", tone: "danger" };
  }
  return { label: "Pendente", tone: "warning" };
}

function getSourceLabel(source) {
  if (source === "firms_automated") return "FIRMS Automático";
  if (source === "manual") return "Manual";
  return source || "—";
}

/**
 * Modal de detalhes da solicitação de alerta.
 * Ações: Criar alerta, Rejeitar, Marcar como recebido, Ver Alerta (se resolution=created).
 */
export default function RequestModal({
  req,
  onClose,
  onCreateAlert,
  onReject,
  onViewAlert,
  viewingAlert = false,
  markingReceived = false,
  onMarkedReceived,
  canCreateAlert = true,
}) {
  const [detail, setDetail] = useState(req);

  useEffect(() => {
    setDetail(req);
    if (!req?.id) return undefined;
    let cancelled = false;
    guardianAlertService
      .getAlertRequest(req.id)
      .then((data) => {
        if (!cancelled && data) setDetail((current) => ({ ...current, ...data }));
      })
      .catch((err) => {
        console.error("Erro ao carregar detalhes da solicitação:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [req]);

  useEffect(() => {
    if (!req) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [req, onClose]);

  if (!detail) return null;

  const area = detail.area_detail;
  const user =
    detail.requested_by && typeof detail.requested_by === "object"
      ? detail.requested_by
      : null;
  const vertResponsible =
    detail.vert_responsible_detail && typeof detail.vert_responsible_detail === "object"
      ? detail.vert_responsible_detail
      : null;
  const receivedBy =
    detail.received_by_detail && typeof detail.received_by_detail === "object"
      ? detail.received_by_detail
      : null;
  const alertType = detail.alert_type_detail;
  const resolution = getResolutionMeta(detail.resolution);
  const isResolved = Boolean(detail.resolution);
  const isCreated = detail.resolution === "created";
  const isManual = detail.request_source === "manual";
  const areaName = area?.name || detail.area_name || "—";
  const projectTitle =
    area?.project_detail?.title || detail.project_title || "—";
  const personName = (person) =>
    person?.full_name || person?.email || "—";

  const handleMarkReceived = async () => {
    try {
      await guardianAlertService.markAsReceived(detail.id, user?.id);
      const next = {
        ...detail,
        received: true,
        received_at: new Date().toISOString(),
        received_by_id: user?.id,
      };
      onMarkedReceived?.(next);
      onClose?.();
    } catch (err) {
      console.error("Erro ao marcar como recebido:", err);
      window.alert("Erro ao marcar como recebido.");
    }
  };

  return (
    <Overlay
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <ModalContainer
        role="dialog"
        aria-modal="true"
        aria-labelledby="alert-request-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <HeaderText>
            <ModalTitle id="alert-request-modal-title">
              Solicitação de alerta
            </ModalTitle>
            <ModalSubtitle>
              {detail.cod_alerta ? (
                <>
                  Código <strong>{detail.cod_alerta}</strong>
                  {alertType?.description ? ` · ${alertType.description}` : ""}
                </>
              ) : (
                alertType?.description || "Solicitação de alerta"
              )}
            </ModalSubtitle>
            <BadgeRow>
              <StatusBadge $tone={resolution.tone}>
                Resolução: {resolution.label}
              </StatusBadge>
              <StatusBadge $tone={detail.received ? "success" : "warning"}>
                {detail.received ? "Recebido" : "Recebimento pendente"}
              </StatusBadge>
              <StatusBadge $tone="info">
                {getSourceLabel(detail.request_source)}
              </StatusBadge>
            </BadgeRow>
          </HeaderText>
          <CloseButton type="button" onClick={onClose} aria-label="Fechar">
            ✕
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <Section>
            <SectionTitle>Informações do alerta</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Código</InfoLabel>
                <InfoValue>{detail.cod_alerta || "—"}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Tipo</InfoLabel>
                <InfoValue>{alertType?.description || "—"}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Origem</InfoLabel>
                <InfoValue>{getSourceLabel(detail.request_source)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Local</InfoLabel>
                <InfoValue>{detail.location || "—"}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Solicitado em</InfoLabel>
                <InfoValue>
                  {formatDate(detail.requested_at || detail.date)}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Último lembrete</InfoLabel>
                <InfoValue>{formatDate(detail.last_reminder_sent_at)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Recebido em</InfoLabel>
                <InfoValue>{formatDate(detail.received_at)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Responsável VERT</InfoLabel>
                <InfoValue>{personName(vertResponsible)}</InfoValue>
              </InfoItem>
            </InfoGrid>

            <DescriptionBlock>
              <InfoLabel>Descrição</InfoLabel>
              <DescriptionText>
                <RichTextView
                  value={detail.description_html || detail.description}
                  fallback="—"
                />
              </DescriptionText>
            </DescriptionBlock>

            {detail.other_description ? (
              <DescriptionBlock>
                <InfoLabel>Descrição complementar</InfoLabel>
                <DescriptionText>{detail.other_description}</DescriptionText>
              </DescriptionBlock>
            ) : null}
          </Section>

          <Section>
            <SectionTitle>Área / Projeto</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Área</InfoLabel>
                <InfoValue>{areaName}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Projeto</InfoLabel>
                <InfoValue>{projectTitle}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>

          {isManual ? (
            <Section>
              <SectionTitle>Criado por</SectionTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Nome</InfoLabel>
                  <InfoValue>{personName(user)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>E-mail</InfoLabel>
                  <InfoValue>{user?.email || "—"}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Telefone</InfoLabel>
                  <InfoValue>{user?.phone || "—"}</InfoValue>
                </InfoItem>
              </InfoGrid>
            </Section>
          ) : null}

          {receivedBy ? (
            <Section>
              <SectionTitle>Confirmação de recebimento</SectionTitle>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Confirmado por</InfoLabel>
                  <InfoValue>{personName(receivedBy)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>E-mail</InfoLabel>
                  <InfoValue>{receivedBy.email || "—"}</InfoValue>
                </InfoItem>
              </InfoGrid>
            </Section>
          ) : null}
        </ModalBody>

        <ModalFooter>
          <FooterButton type="button" $variant="ghost" onClick={onClose}>
            Fechar
          </FooterButton>

          {!detail.received ? (
            <FooterButton
              type="button"
              $variant="ghost"
              disabled={markingReceived}
              onClick={handleMarkReceived}
            >
              {markingReceived ? "Marcando…" : "Marcar como recebido"}
            </FooterButton>
          ) : null}

          <FooterButton
            type="button"
            $variant="reject"
            disabled={isResolved}
            onClick={() => onReject?.(detail)}
          >
            Rejeitar
          </FooterButton>

          {isCreated ? (
            <FooterButton
              type="button"
              disabled={viewingAlert}
              onClick={() => onViewAlert?.(detail)}
            >
              {viewingAlert ? "Abrindo…" : "Ver Alerta"}
            </FooterButton>
          ) : canCreateAlert ? (
            <FooterButton
              type="button"
              disabled={isResolved}
              onClick={() => onCreateAlert?.(detail)}
            >
              Criar alerta
            </FooterButton>
          ) : null}
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  );
}
